import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { XIcon } from "lucide-react";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

import { RevokeInvitationSchema, revokeInvitationSchema } from "@/features/projects/schemas";
import { revokeInvitation } from "@/features/projects/services/project";
import { ProjectInvitation } from "@/features/projects/types";
import Error from "@/shared/components/Error";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

interface RevokeInvitationFormProps {
  projectId: string;
  invitation: ProjectInvitation;
}

export default function RevokeInvitationForm({ projectId, invitation }: RevokeInvitationFormProps) {
  const queryClient = useQueryClient();

  const form = useForm<RevokeInvitationSchema>({
    resolver: zodResolver(revokeInvitationSchema),
    defaultValues: {
      projectId,
      invitationId: invitation.id,
    },
  });

  const mutation = useMutation<undefined, AppError | Error, RevokeInvitationSchema>({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_INVITATIONS, projectId], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_BY_ID, projectId], exact: true });
    },
  });

  async function onSubmit(values: RevokeInvitationSchema) {
    mutation.mutate(values);
  }

  return (
    <AlertDialog
      onOpenChange={(open) => {
        if (!open) mutation.reset();
      }}
    >
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon-sm" className="text-muted-foreground hover:text-destructive">
          <XIcon className="size-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <AlertDialogHeader>
              <AlertDialogTitle>Revoke invite?</AlertDialogTitle>
              <AlertDialogDescription>
                The invitation sent to <span className="font-semibold text-foreground">{invitation.emailAddress}</span> will be cancelled and the link will no longer work.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {mutation.isError && <Error title="Unable to revoke invitation" error={mutation.error} />}
            <Controller
              name="projectId"
              control={form.control}
              render={({ field }) => (
                <Field>
                  <Input type="hidden" {...field} />
                </Field>
              )}
            />
            <Controller
              name="invitationId"
              control={form.control}
              render={({ field }) => (
                <Field>
                  <Input type="hidden" {...field} />
                </Field>
              )}
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={mutation.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction type="submit" className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-40" disabled={mutation.isPending}>
                <XIcon />
                {mutation.isPending ? "Revoking..." : "Revoke invite"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </form>
        </FormProvider>
      </AlertDialogContent>
    </AlertDialog>
  );
}
